import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApi } from '../apiHelpers';
import { ProgrammaticResult, AnalysisResponse, Severity, SEVERITY_CONFIG } from './types';
import { MONO, MOTION, PANEL, PANEL_HEADER, PANEL_TITLE, QUIET_ACTION, SEVERITY_TONE, SkeletonRows } from './styles';
import StatusGrid from './panels/StatusGrid';
import FindingsPanel from './panels/FindingsPanel';
import SummaryTable from './panels/SummaryTable';
import MetricsCards, { extractMetrics } from './panels/MetricsCards';
import ComparisonTable from './panels/ComparisonTable';
import FlightPathMap, { DroneTrack, FlightPoint, DRONE_COLORS } from './panels/FlightPathMap';
import { uploadUlg } from './uploadLog';
import { fetchSeries } from './explorer/seriesClient';
import { buttonClass } from '../components/ui/Button';
import { Checkbox } from '../components/ui/Checkbox';
import { useFileDrop, DropVeil } from '../components/ui/DropZone';
import { Field } from '../components/ui/Field';
import { useFilePicker } from '../components/ui/FilePick';
import Tag from '../components/ui/Tag';
import EmptyState from '../components/ui/EmptyState';
import Progress from '../components/ui/Progress';
import Skeleton from '../components/ui/Skeleton';
import Reveal from '../components/ui/Reveal';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';

/** The verdict landing: the page's one charm moment. A short settle, never on first paint. */
const VerdictChip: React.FC<{ severity: Severity; settle: boolean }> = ({ severity, settle }) => {
  const [settled, setSettled] = useState(!settle);
  useEffect(() => {
    if (settled) return;
    const raf = requestAnimationFrame(() => setSettled(true));
    return () => cancelAnimationFrame(raf);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <span
      className={`inline-flex transition-[transform,opacity] duration-300 ease-emphasized motion-reduce:transition-none ${
        settled ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
      }`}
    >
      <Tag tone={SEVERITY_TONE[severity] || 'neutral'}>
        {SEVERITY_CONFIG[severity]?.label ?? severity}
      </Tag>
    </span>
  );
};

interface RecentLog {
  id: string;
  title: string;
  description: string;
  date: string;
  source: string;
}

interface AnalysisWorkspaceProps {
  logId: string;
  onLogIdChange: (id: string) => void;
  onAnalyze: (question?: string) => void;
  onTimeWindowSelect?: (start_s: number, end_s?: number) => void;
  onSerialsChange?: (serials: Record<string, string>) => void;
  isAnalyzing: boolean;
  hasMessages: boolean;
  programmaticResult?: ProgrammaticResult | null;
  conversationResult?: AnalysisResponse | null;
  comparisonResult?: any;
  overallSeverity?: Severity;
  focusTime?: number | null;
}

const AnalysisWorkspace: React.FC<AnalysisWorkspaceProps> = ({
  logId, onLogIdChange, onAnalyze, onTimeWindowSelect, onSerialsChange, isAnalyzing, hasMessages,
  programmaticResult, conversationResult, comparisonResult, overallSeverity,
  focusTime,
}) => {
  const api = useApi();
  // List always visible — no expand/collapse
  const [recentLogs, setRecentLogs] = useState<RecentLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showManualInput, setShowManualInput] = useState(false);
  // Upload a log straight from here. The road (direct to the box, or through the API)
  // is picked per upload by uploadLog.ts; the status line says which, and the result.
  const [upload, setUpload] = useState<{ phase: 'idle' | 'uploading' | 'done' | 'error'; pct: number; road?: 'direct' | 'api'; message?: string }>({ phase: 'idle', pct: 0 });
  const [flightTracks, setFlightTracks] = useState<DroneTrack[]>([]);
  const [showMap, setShowMap] = useState(false);
  // A pressed status tile narrows the findings to its category; a new result clears it.
  const [focusCategory, setFocusCategory] = useState<string | null>(null);
  useEffect(() => { setFocusCategory(null); }, [programmaticResult]);
  // Dropping a .ulg on the log list uploads it; an encrypted Kiwi log drops with its key file.
  // Serial + firmware cache: localStorage-backed so we never fetch the same log twice
  const SERIAL_STORAGE_KEY = 'analyst_serial_cache';
  const FIRMWARE_STORAGE_KEY = 'analyst_firmware_cache';
  const [serialCache, setSerialCache] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem(SERIAL_STORAGE_KEY) || '{}'); } catch { return {}; }
  });
  const [firmwareCache, setFirmwareCache] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem(FIRMWARE_STORAGE_KEY) || '{}'); } catch { return {}; }
  });

  // Persist caches to localStorage
  useEffect(() => {
    try { localStorage.setItem(SERIAL_STORAGE_KEY, JSON.stringify(serialCache)); } catch { /* full */ }
  }, [serialCache]);
  useEffect(() => {
    try { localStorage.setItem(FIRMWARE_STORAGE_KEY, JSON.stringify(firmwareCache)); } catch { /* full */ }
  }, [firmwareCache]);

  // Notify parent of selected serials
  useEffect(() => {
    const selected: Record<string, string> = {};
    selectedIds.forEach(id => { if (serialCache[id]) selected[id] = serialCache[id]; });
    onSerialsChange?.(selected);
  }, [serialCache, selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRecentLogs = useCallback(async () => {
    setLoadingLogs(true);
    await api.get('/analysis/recent-logs?limit=50', {
      onSuccess: (data: any) => setRecentLogs(data.logs || []),
    });
    setLoadingLogs(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchRecentLogs(); }, [fetchRecentLogs]);

  const handleUploadFile = useCallback(async (picked: FileList | File[] | null | undefined, droneSerial?: string) => {
    const file = picked ? Array.from(picked) : [];
    if (file.length === 0) return;
    setUpload({ phase: 'uploading', pct: 0 });
    try {
      let result;
      try {
        result = await uploadUlg(file, {
          droneSerial,
          onProgress: (pct) => setUpload(u => ({ ...u, pct })),
          onRoad: (road) => setUpload(u => ({ ...u, road })),
        });
      } catch (err: any) {
        // Flight Review (the Firefly store) files a log under a drone and cannot
        // read the serial itself; the analyst store can. Ask once, then retry.
        if (!droneSerial && /drone_serial is required/.test(err?.message || '')) {
          const serial = window.prompt('Drone serial for this log (Flight Review files it under the drone):');
          if (!serial) throw new Error('Upload cancelled: a drone serial is needed');
          return handleUploadFile(file, serial.trim().toUpperCase());
        }
        throw err;
      }
      setUpload({ phase: 'done', pct: 100, message: result.message });
      await fetchRecentLogs();
      setShowManualInput(false);
      setSelectedIds(new Set([result.log_id]));
    } catch (err: any) {
      setUpload({ phase: 'error', pct: 0, message: err?.message || 'Upload failed' });
    }
  }, [fetchRecentLogs]);

  const picker = useFilePicker({ onFiles: (files) => handleUploadFile(files), accept: '.ulg,.ulgc,.ulgk', multiple: true });
  const drop = useFileDrop({ onFiles: (files) => handleUploadFile(files), multiple: true });

  // Background serial fetcher — runs during browser idle time ONLY when no logs are
  // selected. Foreground log-chart/GPS requests always take priority.
  const serialQueueRef = useRef<string[]>([]);
  const serialIdleHandleRef = useRef<number | null>(null);
  const serialPausedRef = useRef<boolean>(false);

  const cancelIdleWork = useCallback(() => {
    if (serialIdleHandleRef.current !== null) {
      if (typeof (window as any).cancelIdleCallback === 'function') {
        (window as any).cancelIdleCallback(serialIdleHandleRef.current);
      } else {
        clearTimeout(serialIdleHandleRef.current);
      }
      serialIdleHandleRef.current = null;
    }
  }, []);

  const scheduleIdle = useCallback((fn: () => void, timeout = 5000) => {
    const ric = (window as any).requestIdleCallback;
    if (typeof ric === 'function') {
      serialIdleHandleRef.current = ric(fn, { timeout });
    } else {
      // Safari fallback
      serialIdleHandleRef.current = window.setTimeout(fn, 500) as unknown as number;
    }
  }, []);

  const fetchNextSerial = useCallback(() => {
    serialIdleHandleRef.current = null;
    if (serialPausedRef.current) return;

    // Skip cached entries — advance the queue until we find an uncached id
    while (serialQueueRef.current.length > 0 && serialCache[serialQueueRef.current[0]]) {
      serialQueueRef.current.shift();
    }
    const id = serialQueueRef.current.shift();
    if (!id) return;

    api.get(`/analysis/log-serial/${id}`, {
      onSuccess: (data: any) => {
        if (data.serial) setSerialCache(prev => ({ ...prev, [id]: data.serial }));
        if (data.firmware_branch) setFirmwareCache(prev => ({ ...prev, [id]: data.firmware_commit ? `${data.firmware_branch} @${data.firmware_commit}` : data.firmware_branch }));
        if (!serialPausedRef.current && serialQueueRef.current.length > 0) {
          scheduleIdle(fetchNextSerial);
        }
      },
      onError: () => {
        if (!serialPausedRef.current && serialQueueRef.current.length > 0) {
          scheduleIdle(fetchNextSerial, 10000);
        }
      },
    });
  }, [serialCache, scheduleIdle]); // eslint-disable-line react-hooks/exhaustive-deps

  // Populate the queue from recent logs (but don't start fetching yet)
  useEffect(() => {
    serialQueueRef.current = recentLogs
      .filter(log => !serialCache[log.id])
      .map(log => log.id);
    // Kick off only if not paused (no log selected) and no fetch already scheduled
    if (!serialPausedRef.current && !serialIdleHandleRef.current && serialQueueRef.current.length > 0) {
      scheduleIdle(fetchNextSerial);
    }
    return () => { cancelIdleWork(); };
  }, [recentLogs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pause/resume the background queue based on user activity.
  // Paused while any log is selected — foreground GPS/chart/analysis requests get priority.
  useEffect(() => {
    const shouldPause = selectedIds.size > 0;
    serialPausedRef.current = shouldPause;
    if (shouldPause) {
      cancelIdleWork();
    } else if (serialQueueRef.current.length > 0 && !serialIdleHandleRef.current) {
      scheduleIdle(fetchNextSerial);
    }
  }, [selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleLogSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Sync selectedIds → logId, auto-fetch GPS for selected logs
  useEffect(() => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      onLogIdChange('');
      return;
    }
    onLogIdChange(ids.length === 1 ? ids[0] : ids.join(','));

    // Remove tracks for deselected logs
    setFlightTracks(prev => prev.filter(t => selectedIds.has(t.id)));

    // Fetch GPS + serial for newly selected logs
    ids.forEach((id, idx) => {
      if (fetchedGpsRef.current.has(id)) return;
      fetchedGpsRef.current.add(id);
      setShowMap(true);

      // Serial
      api.get(`/analysis/log-serial/${id}`, {
        onSuccess: (data: any) => {
          if (data.serial) {
            const short = data.serial.split('-').slice(-2).join('-');
            setSerialCache(prev => ({ ...prev, [id]: data.serial }));
            setFlightTracks(prev => prev.map(t => t.id === id ? { ...t, label: short } : t));
          }
        },
      });

      // GPS: vehicle_gps_position in raw units (1e-7 deg, mm), else sensor_gps in degrees and meters
      const addTrack = (raw: FlightPoint[]) => {
        const valid = raw.filter(p => p.lat !== 0 && p.lon !== 0);
        const gnd = valid.length > 0 ? valid[0].alt : 0;
        const points = valid.map(p => ({ ...p, alt: Math.round((p.alt - gnd) * 10) / 10 }));
        if (points.length > 0) {
          setFlightTracks(prev => [...prev, {
            id, label: serialCache[id]?.split('-').slice(-2).join('-') || id.slice(0, 8),
            color: DRONE_COLORS[idx % DRONE_COLORS.length], points, visible: true,
          }]);
        }
      };
      // A missing lat or lon reads as 0, which the zero filter drops.
      const num = (v: number | null | undefined) => (v == null ? 0 : v);
      fetchSeries(api, id, { topic: 'vehicle_gps_position', instance: 0, fields: ['lat', 'lon', 'alt'], points: 4000, mode: 'rows' })
        .then(
          data => (data.fields?.lat && data.t ? data : null),
          (err: any) => { if (err?.status === 404) return null; throw err; },
        )
        .then(data => {
          if (data) {
            addTrack(data.t.map((t, i) => ({
              lat: num(data.fields.lat[i]) / 1e7, lon: num(data.fields.lon[i]) / 1e7,
              alt: num(data.fields.alt?.[i]) / 1e3, timestamp_s: t,
            })));
            return;
          }
          return fetchSeries(api, id, {
            topic: 'sensor_gps', instance: 0, fields: ['latitude_deg', 'longitude_deg', 'altitude_msl_m'], points: 4000, mode: 'rows',
          }).then(data2 => {
            if (!data2.fields?.latitude_deg || !data2.t) return;
            addTrack(data2.t.map((t, i) => ({
              lat: num(data2.fields.latitude_deg[i]), lon: num(data2.fields.longitude_deg?.[i]),
              alt: num(data2.fields.altitude_msl_m?.[i]), timestamp_s: t,
            })));
          });
        })
        .catch(() => {});
    });
  }, [selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const verdict: Severity | undefined = overallSeverity
    ?? (programmaticResult?.per_category ? programmaticResult.severity : undefined);
  // A verdict already on screen at first paint (a restored session) must not animate.
  const firstPaintRef = useRef(true);
  useEffect(() => { firstPaintRef.current = false; }, []);

  // Collapse selector once analysis has started (has messages or results)
  // Fetch GPS data for flight path map when analysis starts
  // Fetch GPS when "Show on Map" is clicked
  const fetchedGpsRef = useRef<Set<string>>(new Set());
  const handleToggleTrack = useCallback((id: string) => {
    setFlightTracks(prev => prev.map(t => t.id === id ? { ...t, visible: !t.visible } : t));
  }, []);

  // Helper: get track color for a log id
  const getTrackColor = (id: string) => {
    const track = flightTracks.find(t => t.id === id);
    return track?.color || null;
  };

  const listEmpty = !loadingLogs && recentLogs.length === 0;

  return (
    <div className="flex h-[60dvh] min-w-0 flex-col gap-2 md:h-auto md:min-h-0 md:w-[300px] md:flex-shrink-0">
      {/* ── 3D/2D FLIGHT PATH (always reserves space) ── */}
      <div className="flex min-h-[200px] min-w-0 flex-[2] flex-col overflow-hidden rounded-lg bg-surface-container-low">
        {showMap && flightTracks.length > 0 ? (
          <FlightPathMap
            tracks={flightTracks}
            onToggleTrack={handleToggleTrack}
            onTimeWindowSelect={onTimeWindowSelect}
            externalFocusTime={focusTime}
          />
        ) : selectedIds.size > 0 ? (
          <div className="flex flex-1 flex-col justify-end gap-2 p-4" role="status">
            <div aria-hidden="true" className="flex flex-1 items-end gap-1.5">
              {['h-[38%]', 'h-[55%]', 'h-[47%]', 'h-[70%]', 'h-[62%]', 'h-[80%]', 'h-[58%]', 'h-[66%]', 'h-[44%]', 'h-[52%]'].map((h, i) => (
                <Skeleton key={i} className={`flex-1 ${h}`} />
              ))}
            </div>
            <span className="text-sm text-on-surface-variant">Loading the flight path</span>
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center p-6">
            <p className="max-w-xs text-center text-sm text-on-surface-variant">
              Pick a flight from the list, paste its id, or upload a log.
            </p>
          </div>
        )}
      </div>

      {/* ── Bottom panel (log list + results) ── */}
      <div className="relative min-h-0 min-w-0 flex-1">
        <div className="absolute inset-0 flex min-w-0 flex-col gap-2 overflow-y-auto overflow-x-hidden [&>*]:flex-shrink-0">

      {/* ── LOG SELECTOR (hidden when comparison results are showing) ── */}
      {!comparisonResult?.comparison && (
        <FadeIn key="selector" className="h-full">
        <div
          className={`${PANEL} relative h-full`}
          {...drop.bind}
        >
          <DropVeil show={drop.dragging} label="Drop to upload the log" />
          <div className={PANEL_HEADER}>
            <div className="flex min-w-0 items-baseline gap-2">
              <h2 className={PANEL_TITLE}>Flight logs</h2>
              <Appear show={selectedIds.size > 0} className="whitespace-nowrap text-xs text-on-surface-variant tabular-nums">
                {selectedIds.size} selected
              </Appear>
            </div>
            <span className="flex flex-shrink-0 items-center gap-1">
            {picker.input}
            <button
              type="button"
              onClick={picker.open}
              disabled={upload.phase === 'uploading'}
              className={buttonClass(listEmpty ? 'filled' : 'quiet')}
            >
              {upload.phase === 'uploading'
                ? <span className="tabular-nums">Uploading {upload.pct}%</span>
                : 'Upload log'}
            </button>
            <button
              type="button"
              onClick={() => setShowManualInput(!showManualInput)}
              aria-pressed={showManualInput}
              className={QUIET_ACTION}
            >
              {showManualInput ? 'Show list' : 'Paste an id'}
            </button>
            </span>
          </div>
          <Reveal show={upload.phase !== 'idle'}>
            <div role="status" className="flex-shrink-0 bg-surface-container px-3 pb-2">
              <p className={`text-xs ${upload.phase === 'error' ? 'text-red-400' : 'text-on-surface-variant'}`}>
                {upload.phase === 'uploading' && <span className="tabular-nums">{`Uploading${upload.road === 'direct' ? ' straight to the analyst' : upload.road === 'api' ? ' through the API' : ''}: ${upload.pct}%`}</span>}
                {upload.phase === 'done' && (upload.message || 'Uploaded. The flight is selected below.')}
                {upload.phase === 'error' && (upload.message || 'Upload failed. Check the file is a .ulg, or an encrypted log with its key file, and try again.')}
              </p>
              {upload.phase === 'uploading' && (
                <Progress className="mt-1.5" size="xs" value={upload.pct} max={100} label="Upload progress" />
              )}
            </div>
          </Reveal>

          {/* Manual UUID input (toggled) */}
          {showManualInput && (
            <FadeIn key="manual">
            <div className="p-3">
              <Field
                type="text"
                value={logId}
                onChange={(e) => { onLogIdChange(e.target.value.trim()); setSelectedIds(new Set()); }}
                placeholder="Paste a log id, or several separated by commas"
                aria-label="Log ids"
                spellCheck={false}
                className={MONO}
                onKeyDown={(e) => { if (e.key === 'Enter' && logId) onAnalyze(); }}
              />
            </div>
            </FadeIn>
          )}

          {/* Recent logs list */}
          {!showManualInput && (
            <FadeIn key="list" className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div className="dt-scroll min-h-0 min-w-0 flex-1 overflow-auto">
              {loadingLogs ? (
                <SkeletonRows rows={6} />
              ) : recentLogs.length === 0 ? (
                <EmptyState variant="area">
                  No flights yet. Upload a .ulg to start the list. An encrypted log goes in together with its key file.
                </EmptyState>
              ) : (
                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr>
                      <th className="sticky top-0 z-[1] w-8 bg-surface-container-high px-2 py-1.5">
                        <span className="sr-only">Selected</span>
                      </th>
                      {['Serial', 'Date', 'Description', 'Firmware', 'Duration', 'Modes'].map(h => (
                        <th key={h} className="sticky top-0 z-[1] whitespace-nowrap bg-surface-container-high px-2 py-1.5 text-left font-medium text-on-surface-variant">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recentLogs.map(log => {
                      const isSelected = selectedIds.has(log.id);
                      const trackColor = getTrackColor(log.id);
                      const dur = (log as any).duration_s || 0;
                      const modes: string[] = (log as any).flight_modes || [];
                      const d = log.date ? new Date(log.date) : null;
                      const dateStr = d ? `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${String(d.getFullYear()).slice(-2)}` : '';
                      return (
                        <tr
                          key={log.id}
                          onClick={() => toggleLogSelection(log.id)}
                          // The bar is the drone's own track color on the map, so it stays inline.
                          style={isSelected && trackColor ? { boxShadow: `inset 3px 0 0 0 ${trackColor}` } : undefined}
                          className={`cursor-pointer ${MOTION} ${
                            isSelected
                              ? `bg-surface-container-high ${trackColor ? '' : 'shadow-[inset_3px_0_0_0_theme(colors.primary)]'}`
                              : 'hover:bg-surface-container-high'
                          }`}
                        >
                          <td className="px-2 py-1.5 text-center">
                            <Checkbox
                              size="sm"
                              checked={isSelected}
                              onChange={() => toggleLogSelection(log.id)}
                              labelProps={{ onClick: (e) => e.stopPropagation() }}
                              aria-label={`Select log ${serialCache[log.id] || log.id}`}
                            />
                          </td>
                          <td className={`whitespace-nowrap px-2 py-1.5 text-on-surface ${MONO}`}>
                            {serialCache[log.id] || <span className="text-on-surface-variant">{'\u2014'}</span>}
                          </td>
                          <td className={`whitespace-nowrap px-2 py-1.5 text-on-surface-variant tabular-nums ${MONO}`}>
                            {dateStr}
                          </td>
                          <td className="max-w-[250px] truncate px-2 py-1.5 text-on-surface">
                            {log.description || <span className="italic text-on-surface-variant">No description</span>}
                          </td>
                          <td className={`whitespace-nowrap px-2 py-1.5 text-on-surface-variant ${MONO}`}>
                            {(() => {
                              // "branch @commit" from the serial fetch, else the listing's own
                              // branch plus commit (the analyst store lists both; Flight Review
                              // lists the branch only until the fetch fills it in)
                              const cached = firmwareCache[log.id];
                              const raw = cached || (log as any).software || '';
                              const [branch, commit] = cached && cached.includes(' @')
                                ? cached.split(' @')
                                : [raw.replace(/\s+\d+$/, ''), (log as any).firmware_commit || ''];
                              if (!branch) return '\u2014';
                              return (
                                <>
                                  {branch}
                                  {commit && <span className="ml-1.5 text-xs opacity-70">@{commit}</span>}
                                </>
                              );
                            })()}
                          </td>
                          <td className={`whitespace-nowrap px-2 py-1.5 text-on-surface-variant tabular-nums ${MONO}`}>
                            {dur > 0 ? `${Math.floor(dur / 60)}:${String(dur % 60).padStart(2, '0')}` : '\u2014'}
                          </td>
                          <td className="whitespace-nowrap px-2 py-1.5">
                            <span className="flex gap-1">
                              {modes.map((m, i) => (
                                <Tag key={i} size="sm" variant={m === 'Show' ? 'code' : 'status'}>
                                  {m}
                                </Tag>
                              ))}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            </FadeIn>
          )}

          {/* Status bar */}
          <Reveal show={selectedIds.size > 0}>
            <div className="flex flex-shrink-0 items-center gap-2 bg-surface-container px-3 py-1.5 text-xs text-on-surface-variant">
              <span className="tabular-nums">{selectedIds.size} log{selectedIds.size > 1 ? 's' : ''} selected</span>
              <button
                type="button"
                onClick={() => { setSelectedIds(new Set()); onLogIdChange(''); }}
                className={`${buttonClass('quiet', 'sm')} ml-auto`}
              >
                Clear selection
              </button>
            </div>
          </Reveal>
        </div>
        </FadeIn>
      )}

      {/* ── Loading state ── */}
      <Reveal show={isAnalyzing && !hasMessages}>
        <div className={`${PANEL} items-center px-5 py-10 text-center`} role="status">
          <p className="text-sm font-semibold text-on-surface">The analyst is reading this flight</p>
          <p className="mt-1 text-xs text-on-surface-variant">
            Reading the log, pulling topic data, and checking for anomalies.
          </p>
        </div>
      </Reveal>

      {/* ── Programmatic results (only when full analysis exists, not just serial) ── */}
      <Reveal show={!!verdict}>
        <div className="flex min-w-0 flex-shrink-0 items-center gap-2 px-1 pt-1">
          <span className="text-sm text-on-surface-variant">Verdict</span>
          {verdict && <VerdictChip key={verdict} severity={verdict} settle={!firstPaintRef.current} />}
        </div>
      </Reveal>
      <Reveal show={!!programmaticResult?.per_category}>
        {programmaticResult && programmaticResult.per_category && (
        <div className="flex min-w-0 flex-col gap-2.5">
          <MetricsCards metrics={extractMetrics(programmaticResult)} />
          <StatusGrid
            categories={programmaticResult.per_category}
            activeCategory={focusCategory}
            onCategoryClick={(c) => setFocusCategory(prev => (prev === c ? null : c))}
          />
          {programmaticResult.summary_table && programmaticResult.summary_table.length > 0 && (
            <SummaryTable rows={programmaticResult.summary_table} />
          )}
          <FindingsPanel
            categories={programmaticResult.per_category}
            focusCategory={focusCategory}
            onClearFocus={() => setFocusCategory(null)}
          />
        </div>
        )}
      </Reveal>

      {/* ── Comparison results ── */}
      {comparisonResult?.comparison && (
        <FadeIn key="comparison">
        <ComparisonTable
          logs={comparisonResult.logs || []}
          comparison={comparisonResult.comparison || []}
          commonIssues={comparisonResult.summary?.common_issues}
          keyDifferences={comparisonResult.summary?.key_differences}
        />
        </FadeIn>
      )}
        </div>
      </div>
    </div>
  );
};

export default AnalysisWorkspace;
